<template>
    <v-card class="form-card">
        <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
            Pagamento do motorista
            <v-spacer />
            <v-chip small outlined :color="isPartner ? 'orange darken-2' : 'primary'">
                {{ isPartner ? 'Parceiro' : 'Funcionário' }}
            </v-chip>
        </v-card-title>

        <v-card-text>
            <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

            <!-- PARCEIRO: periodicidade do fechamento -->
            <template v-if="isPartner">
                <p class="body-2 grey--text">
                    Define o período de cada fechamento. O valor de cada viagem vem da tabela "Valores por localidade".
                </p>
                <v-row dense>
                    <v-col cols="12" md="4">
                        <v-select dense outlined hide-details label="Periodicidade" :items="periodTypes"
                            v-model="settings.period_type" :disabled="!canManage" />
                    </v-col>
                    <v-col v-if="settings.period_type === 'weekly'" cols="12" md="4">
                        <v-select dense outlined hide-details label="Fecha toda" :items="weekdays"
                            v-model="settings.closing_weekday" :disabled="!canManage" />
                    </v-col>
                    <v-col v-if="settings.period_type === 'monthly'" cols="12" md="4">
                        <v-text-field dense outlined hide-details type="number" min="1" max="31"
                            label="Dia do fechamento (vazio = mês fechado)" v-model="settings.closing_day"
                            :disabled="!canManage" />
                    </v-col>
                    <v-col v-if="settings.period_type === 'biweekly'" cols="12" md="4" class="d-flex align-center">
                        <span class="body-2 grey--text">Dias 1 a 15 e 16 ao fim do mês</span>
                    </v-col>
                </v-row>
            </template>

            <!-- DADOS DE PAGAMENTO (ambos) -->
            <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                    <v-select dense outlined hide-details clearable label="Forma de pagamento" :items="paymentMethods"
                        v-model="settings.payment_method" :disabled="!canManage" />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field dense outlined hide-details label="Chave PIX" v-model="settings.pix_key"
                        :disabled="!canManage" />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                    <v-btn v-if="canManage" color="primary" elevation="0" :loading="saving" @click="saveSettings">
                        Salvar
                    </v-btn>
                </v-col>
            </v-row>

            <!-- FUNCIONÁRIO: salário com vigência -->
            <template v-if="!isPartner">
                <h4 class="mt-6 mb-2">Salário</h4>
                <p class="body-2 grey--text mb-2">
                    O fechamento usa o salário vigente no fim do período. Para reajustar, cadastre um novo valor com a data
                    de início; o histórico é mantido.
                </p>

                <v-simple-table dense class="mb-3">
                    <thead>
                        <tr>
                            <th>Vigente a partir de</th>
                            <th>Salário mensal</th>
                            <th>Encargos</th>
                            <th>Custo para a empresa</th>
                            <th v-if="canManage"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!salaries.length">
                            <td colspan="5" class="grey--text">Nenhum salário cadastrado.</td>
                        </tr>
                        <tr v-for="(salary, index) in salaries" :key="salary.id">
                            <td>
                                {{ formatDate(salary.valid_from) }}
                                <v-chip v-if="index === currentSalaryIndex" x-small color="success" dark class="ml-1">vigente</v-chip>
                            </td>
                            <td>{{ formatMoney(salary.monthly_salary) }}</td>
                            <td>{{ Number(salary.charges_percent) }}%</td>
                            <td>{{ formatMoney(salary.monthly_salary * (1 + Number(salary.charges_percent) / 100)) }}</td>
                            <td v-if="canManage" class="text-right">
                                <v-btn icon x-small color="error" @click="removeSalary(salary)">
                                    <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <v-form v-if="canManage" ref="salaryForm">
                    <v-row dense>
                        <v-col cols="12" md="3">
                            <v-currency-field dense outlined hide-details="auto" prefix="R$" label="Salário mensal"
                                v-model="newSalary.monthly_salary" :rules="[v => Number(v) > 0 || 'Informe o salário']" />
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-text-field dense outlined hide-details="auto" type="number" min="0" suffix="%"
                                label="Encargos (opcional)" v-model="newSalary.charges_percent" />
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-text-field dense outlined hide-details="auto" type="date" label="Vigente a partir de"
                                v-model="newSalary.valid_from" :rules="[v => !!v || 'Informe a data']" />
                        </v-col>
                        <v-col cols="12" md="3" class="d-flex align-center">
                            <v-btn outlined color="primary" :loading="saving" @click="addSalary">
                                <v-icon left small>mdi-plus</v-icon>Adicionar
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

// Configuração de pagamento do motorista (Financeiro > Motoristas a pagar)
export default {
    name: 'DriverPaymentSettings',
    props: {
        driverId: { type: [Number, String], required: true },
        driverType: { type: [Number, String], default: 1 }
    },
    data: () => ({
        loading: false,
        saving: false,
        settings: { period_type: 'monthly', closing_weekday: 5, closing_day: null, payment_method: null, pix_key: null },
        salaries: [],
        newSalary: { monthly_salary: null, charges_percent: null, valid_from: moment().startOf('month').format('YYYY-MM-DD') },
        periodTypes: [
            { value: 'weekly', text: 'Semanal' },
            { value: 'biweekly', text: 'Quinzenal' },
            { value: 'monthly', text: 'Mensal' }
        ],
        weekdays: [
            { value: 0, text: 'Domingo' }, { value: 1, text: 'Segunda' }, { value: 2, text: 'Terça' },
            { value: 3, text: 'Quarta' }, { value: 4, text: 'Quinta' }, { value: 5, text: 'Sexta' }, { value: 6, text: 'Sábado' }
        ],
        paymentMethods: ['PIX', 'Transferência', 'Dinheiro', 'Boleto', 'Outro']
    }),
    computed: {
        isPartner() {
            return Number(this.driverType) === 2
        },
        canManage() {
            return this.$check(['financial.drivers.manage'])
        },
        currentSalaryIndex() {
            const today = moment().format('YYYY-MM-DD')
            return this.salaries.findIndex(salary => salary.valid_from <= today)
        }
    },
    watch: {
        driverId: { immediate: true, handler() { this.load() } }
    },
    methods: {
        apply(content) {
            const settings = content.settings || {}
            this.settings = {
                period_type: settings.period_type || 'monthly',
                closing_weekday: settings.closing_weekday === null || settings.closing_weekday === undefined ? 5 : settings.closing_weekday,
                closing_day: settings.closing_day || null,
                payment_method: settings.payment_method || null,
                pix_key: settings.pix_key || null
            }
            this.salaries = content.salaries || []
        },
        async load() {
            if (!this.driverId) return
            this.loading = true
            try {
                const response = await axios.get(`/api/v2/financial/drivers/${this.driverId}/payment-settings`)
                this.apply(response.data.content)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 })
            } finally {
                this.loading = false
            }
        },
        async saveSettings() {
            this.saving = true
            try {
                const response = await axios.put(`/api/v2/financial/drivers/${this.driverId}/payment-settings`, this.settings)
                this.apply(response.data.content)
                this.$dialog.notify.success('Pagamento do motorista salvo', { position: 'top-right', timeout: 3000 })
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 })
            } finally {
                this.saving = false
            }
        },
        async addSalary() {
            if (!this.$refs.salaryForm.validate()) return
            this.saving = true
            try {
                const response = await axios.post(`/api/v2/financial/drivers/${this.driverId}/salaries`, this.newSalary)
                this.apply(response.data.content)
                this.newSalary = { monthly_salary: null, charges_percent: null, valid_from: moment().startOf('month').format('YYYY-MM-DD') }
                this.$refs.salaryForm.resetValidation()
                this.$dialog.notify.success('Salário cadastrado', { position: 'top-right', timeout: 3000 })
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 })
            } finally {
                this.saving = false
            }
        },
        async removeSalary(salary) {
            const confirmed = await this.$dialog.confirm({
                title: 'Remover salário',
                text: `Remover o salário vigente a partir de ${this.formatDate(salary.valid_from)}?`,
                actions: { false: 'Cancelar', true: 'Remover' }
            })
            if (!confirmed) return

            try {
                const response = await axios.delete(`/api/v2/financial/drivers/${this.driverId}/salaries/${salary.id}`)
                this.apply(response.data.content)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 })
            }
        },
        formatDate(value) {
            return value ? moment(value).format('DD/MM/YYYY') : '-'
        },
        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }
    }
}
</script>
